import type { LiveJoinTarget, OpenLiveSession } from '../types';

function isLiveMatch(status: string | undefined): boolean {
  if (!status) return true;
  return status.toLowerCase() === 'live';
}

function isOpenSession(status: string | undefined): boolean {
  if (!status) return true;
  return status.toLowerCase() === 'open';
}

function sortSessions(sessions: OpenLiveSession[]): OpenLiveSession[] {
  return [...sessions].sort((a, b) => {
    if (a.createdAt && b.createdAt) {
      return Date.parse(b.createdAt) - Date.parse(a.createdAt);
    }
    return 0;
  });
}

function firstLiveMatchTarget(session: OpenLiveSession): LiveJoinTarget | null {
  if (!isOpenSession(session.status)) return null;
  const match = (session.matches ?? []).find((m) => isLiveMatch(m.status));
  if (!match || !session.id || !session.teamId) return null;
  return {
    teamId: session.teamId,
    sessionId: session.id,
    matchId: match.id,
    map: match.map,
  };
}

/**
 * Pick a joinable open session + live match.
 * If preferredTeamId is set, prefer that team's sessions; otherwise any open session.
 */
export function pickJoinTarget(
  sessions: OpenLiveSession[],
  preferredTeamId?: string | null,
): LiveJoinTarget | null {
  if (sessions.length === 0) return null;

  const ordered = sortSessions(sessions);

  if (preferredTeamId) {
    for (const session of ordered) {
      if (session.teamId !== preferredTeamId) continue;
      const target = firstLiveMatchTarget(session);
      if (target) return target;
    }
  }

  for (const session of ordered) {
    const target = firstLiveMatchTarget(session);
    if (target) return target;
  }

  return null;
}

/** True when there is an open session but none has a live match yet. */
export function hasOpenSessionWithoutMatch(
  sessions: OpenLiveSession[],
  preferredTeamId?: string | null,
): boolean {
  const scoped = preferredTeamId
    ? sessions.filter((s) => s.teamId === preferredTeamId)
    : sessions;
  if (scoped.length === 0) return false;
  const open = scoped.filter((s) => isOpenSession(s.status));
  if (open.length === 0) return false;
  return open.every((s) => !(s.matches ?? []).some((m) => isLiveMatch(m.status)));
}

export function buildLivePlayerPath(target: LiveJoinTarget): string {
  return `/live/${encodeURIComponent(target.teamId)}/sessions/${encodeURIComponent(target.sessionId)}/matches/${encodeURIComponent(target.matchId)}`;
}

export function buildLiveSessionPath(teamId: string, sessionId: string): string {
  return `/live/${encodeURIComponent(teamId)}/sessions/${encodeURIComponent(sessionId)}`;
}

export function buildLiveControlsPath(target: LiveJoinTarget): string {
  return `${buildLivePlayerPath(target)}/controls`;
}

/** Latest open session for a team (by createdAt desc), with or without a live match. */
export function pickLatestOpenSession(
  sessions: OpenLiveSession[],
  preferredTeamId?: string | null,
): OpenLiveSession | null {
  const scoped = preferredTeamId
    ? sessions.filter((s) => s.teamId === preferredTeamId)
    : sessions;
  const open = sortSessions(scoped.filter((s) => isOpenSession(s.status)));
  return open[0] ?? null;
}

export function liveMatchOnSession(session: OpenLiveSession): OpenLiveSession['matches'][0] | null {
  return (session.matches ?? []).find((m) => isLiveMatch(m.status)) ?? null;
}
